"use client";

import { useLayoutEffect, useRef } from "react";
import { cn } from "../lib/utils";

export interface TransitionTextProps {
  text: string;
  /** Duration in milliseconds. Initial rendering never animates. */
  duration?: number;
  className?: string;
}

/** Single-line text that expands to its current width and reveals without fading. */
export function TransitionText({
  text,
  duration = 200,
  className,
}: TransitionTextProps) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const previousWidth = useRef<number | null>(null);

  // Synchronize measured DOM widths with the browser animation API before paint.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = textRef.current;

    if (!container || !content) {
      return;
    }

    const width = content.getBoundingClientRect().width;
    const from = previousWidth.current;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const animations: Animation[] = [];

    if (
      from !== null &&
      !reducedMotion.matches &&
      duration > 0 &&
      container.animate
    ) {
      const timing = { duration, easing: "cubic-bezier(.22,1,.36,1)" };
      animations.push(
        container.animate(
          [{ width: `${from}px` }, { width: `${width}px` }],
          timing,
        ),
      );

      // A reveal follows expansion; on contraction the shorter text is readable
      // immediately while the surrounding space closes around it.
      if (width > from) {
        animations.push(
          content.animate(
            [
              { clipPath: "inset(0 100% 0 0)" },
              { clipPath: "inset(0 0% 0 0)" },
            ],
            timing,
          ),
        );
      }
    }

    previousWidth.current = width;

    const stopMotion = () => {
      if (reducedMotion.matches) {
        animations.forEach((animation) => animation.cancel());
      }
    };

    reducedMotion.addEventListener("change", stopMotion);

    return () => {
      // Rapid updates start from the width currently on screen, not an old target.
      if (animations.some((animation) => animation.playState === "running")) {
        previousWidth.current = container.getBoundingClientRect().width;
      }

      animations.forEach((animation) => animation.cancel());
      reducedMotion.removeEventListener("change", stopMotion);
    };
  }, [text, duration]);

  return (
    <span
      ref={containerRef}
      className={cn(
        "inline-block shrink-0 overflow-hidden text-left align-middle",
        className,
      )}
    >
      <span ref={textRef} className="inline-block whitespace-nowrap">
        {text}
      </span>
    </span>
  );
}
